/**
 * EditProfileModal — lets the signed-in user update their displayName
 * and profile picture from the topmenu chip.
 *
 * Newly picked photos are uploaded to Firebase Cloud Storage on save
 * (`avatars/{uid}/avatar.<ext>`); the resulting download URL is mirrored
 * onto the Firebase Auth user and persisted into `appUsers/{uid}` so the
 * photo + name survive across devices. The local data-URL we set on pick
 * is only ever used for the in-modal preview.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Check, Trash2, Upload, X } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useAuth } from '../lib/auth/AuthProvider';
import { useToast } from './ToastProvider';
import { uploadAvatar } from '../lib/storage';

interface Props {
  onClose: () => void;
}

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB cap on uploads.

export function EditProfileModal({ onClose }: Props) {
  const { user, updateProfile } = useAuth();
  const { push } = useToast();
  const [displayName, setDisplayName] = useState<string>(user?.displayName ?? '');
  const [photoURL, setPhotoURL] = useState<string | undefined>(user?.photoURL);
  // The actual File picked this session (uploaded on save), plus a flag for
  // an explicit "remove existing photo" action. `photoURL` is preview-only.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  if (!user) return null;

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Pick an image file (JPG, PNG, GIF, or WebP).');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Image is over 5 MB. Try something smaller.');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setPhotoURL(dataUrl);
    setPhotoFile(file);
    setPhotoRemoved(false);
  };

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      // Resolve what to send for the photo:
      //   - new file picked  → upload to Storage, use download URL
      //   - photo removed     → '' (explicit clear)
      //   - otherwise         → undefined (leave unchanged)
      let nextPhotoURL: string | undefined;
      if (photoFile) {
        nextPhotoURL = await uploadAvatar(user.uid, photoFile);
      } else if (photoRemoved) {
        nextPhotoURL = '';
      } else {
        nextPhotoURL = undefined;
      }

      await updateProfile({
        displayName: displayName.trim() || undefined,
        photoURL: nextPhotoURL,
      });
      push({
        kind: 'success',
        text: 'Profile updated',
        detail: 'Your name and photo are saved and synced across devices.',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save profile.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoURL(undefined);
    setPhotoFile(null);
    setPhotoRemoved(true);
  };

  const initials = (displayName || user.email).slice(0, 1).toUpperCase();

  return createPortal(
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      data-menu-portal="true"
    >
      <div
        ref={containerRef}
        className="account-modal-wrap"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-title"
        tabIndex={-1}
        style={{ width: 'min(540px, 100%)' }}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <header
            className="account-modal__head"
            style={{ alignItems: 'center', borderBottom: 'none', paddingBottom: 4 }}
          >
            <Camera size={18} style={{ color: C.magentaSoft, marginRight: 6 }} />
            <div style={{ flex: 1 }}>
              <span className="t-eyebrow">Profile</span>
              <h2
                id="edit-profile-title"
                className="t-section-title"
                style={{ margin: '4px 0 0' }}
              >
                Edit profile
              </h2>
            </div>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
              disabled={busy}
            >
              <X size={13} />
            </button>
          </header>

          <div style={{ display: 'grid', gap: 18, padding: '12px 0 0' }}>
            {/* Avatar + upload controls */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: 14,
                borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  display: 'inline-grid',
                  placeItems: 'center',
                  fontSize: 24,
                  fontWeight: 700,
                  color: photoURL ? 'transparent' : '#fff',
                  border: '1px solid rgba(255,255,255,0.18)',
                  flexShrink: 0,
                  background: photoURL
                    ? `center/cover no-repeat url("${photoURL}")`
                    : user.role === 'admin'
                      ? 'linear-gradient(135deg, #FF00FF, #BB4198)'
                      : user.role === 'staff'
                        ? 'linear-gradient(135deg, #0082F3, #2F01FD)'
                        : 'linear-gradient(135deg, #FDAD01, #E23939)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
                }}
              >
                {initials}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t-card-title">Profile photo</div>
                <p className="t-meta" style={{ margin: '2px 0 10px' }}>
                  Square images look best. Up to 5&nbsp;MB — JPG, PNG, GIF, or WebP.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="glass-btn"
                    onClick={handlePick}
                    disabled={busy}
                  >
                    <Upload size={13} /> {photoURL ? 'Replace photo' : 'Upload photo'}
                  </button>
                  {photoURL && (
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={handleRemovePhoto}
                      disabled={busy}
                    >
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(f);
                    // Reset so picking the same file again refires.
                    e.currentTarget.value = '';
                  }}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Identity fields */}
            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                Display name
              </div>
              <input
                className="glass-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="What should we call you?"
                disabled={busy}
                maxLength={80}
              />
            </div>

            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                Email
              </div>
              <input
                className="glass-input"
                value={user.email}
                disabled
                style={{ opacity: 0.7, cursor: 'not-allowed' }}
              />
              <p className="t-meta" style={{ margin: '6px 0 0' }}>
                Email is managed by the auth provider. Ask an admin to change it.
              </p>
            </div>

            {error && (
              <div className="login-banner" role="alert">
                {error}
              </div>
            )}
          </div>

          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              paddingTop: 14,
              marginTop: 4,
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={handleSave}
              disabled={busy}
            >
              <Check size={13} /> {busy ? 'Saving…' : 'Save changes'}
            </button>
          </footer>
        </GlassPanel>
      </div>
    </div>,
    document.body,
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}
